import { mdiTextBoxOutline } from '@mdi/js'
import Icon from '@mdi/react'
import MoveToTopIcon from '@mui/icons-material/ArrowUpward'
import SaveIcon from '@mui/icons-material/Check'
import DeleteIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import IconCopy from '@mui/icons-material/FileCopyOutlined'
import UndoIcon from '@mui/icons-material/Undo'
import { ActionsStrip, ActionsStripAction } from 'components/ActionsStrip'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { message } from 'services/snackbar'
import styles from './SavedTexts.pcss'

export const SavedTextListItem = (props: {
  data: { id: string, text: string, title?: string },
  isInEditMode: boolean,
  small?: boolean,
  onStartEdit: (id: string) => void,
  onStopEdit: (id: string) => void,
  onUpdate: (id: string, text: string, title: string) => void,
  onDelete: (id: string) => void,
  onMoveToTop?: (id: string) => void
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const resetValue = React.useRef('')
  const textElementRef = React.useRef<HTMLDivElement>(null)
  const titleElementRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (textElementRef.current) {
      textElementRef.current.innerText = props.data.text
    }
    if (titleElementRef.current) {
      titleElementRef.current.innerText = props.data.title || ''
    }
  }, [props.data.text, props.data.title])

  React.useEffect(() => {
    if (props.isInEditMode && textElementRef.current) {
      const range = new Range()
      range.selectNodeContents(textElementRef.current)
      range.collapse()
      document.getSelection()?.removeAllRanges()
      document.getSelection()?.addRange(range)
    }
  }, [props.isInEditMode])

  const onCopy = () => {
    navigator.clipboard.writeText(textElementRef.current?.innerText || '')
      .then(() => dispatch(message(intl.formatMessage({ id: 'notifications.status-copied' }))))
  }

  const onStartEdit = () => {
    props.onStartEdit(props.data.id)
    resetValue.current = textElementRef.current?.innerText || ''
  }

  const onDelete = () => {
    props.onDelete(props.data.id)
  }

  const onUpdate = () => {
    const text = textElementRef.current?.innerText.trim()
    const title = titleElementRef.current?.innerText.trim() || ''
    if (text) {
      props.onUpdate(props.data.id, text, title)
    }
  }

  const undo = () => {
    props.onStopEdit(props.data.id)
    if (textElementRef.current) {
      textElementRef.current.innerText = resetValue.current
    }
  }

  const onPaste = (event: React.ClipboardEvent) => {
    event.preventDefault()
    // Paste text only, without formatting
    document.execCommand('inserttext', false, event.clipboardData.getData('text/plain'))
  }

  const moveToTop = () => {
    if (props.onMoveToTop) {
      props.onMoveToTop(props.data.id)
    }
  }

  const hideTitle = !props.isInEditMode && !props.data.title

  return (
    <li className={styles.li} data-testid="saved-text">
      <div className={styles['text-wrapper']}>
        <Icon path={mdiTextBoxOutline} size="20px" className={styles['item-icon']} />
        <div className={styles['text-content']}>
          <div
            className={`${styles.editor} ${styles['text-title']} ${hideTitle ? styles['text-title-hidden'] : ''}`
              + `${props.small ? styles.small : ''} ${props.isInEditMode ? styles.editing : ''}`}
            ref={titleElementRef}
            contentEditable={props.isInEditMode}
            data-testid="saved-text-title"
            onPaste={onPaste}
          >
          </div>
          <div
            className={`${styles.editor} ${props.small ? styles.small : ''} ${props.isInEditMode ? styles.editing : ''}`}
            ref={textElementRef}
            contentEditable={props.isInEditMode}
            data-testid="saved-text-content"
            onPaste={onPaste}
          >
          </div>
        </div>
      </div>
      <ActionsStrip
        className={`${styles['editor-actions']} ${props.isInEditMode ? styles.visible : ''}`}
        data-testid="saved-text-actions"
        actions={props.isInEditMode
          ? [
            { icon: <IconCopy />, onClick: onCopy, 'data-testid': 'btn-copy' },
            { icon: <SaveIcon />, onClick: onUpdate, className: styles['btn-save'], 'data-btn-update': true },
            { icon: <UndoIcon />, onClick: undo, 'data-btn-undo': true }
          ] as ActionsStripAction[]
          : [
            { icon: <IconCopy />, onClick: onCopy, 'data-testid': 'btn-copy' },
            { icon: <EditIcon />, onClick: onStartEdit, 'data-btn-edit': true },
            ...(props.onMoveToTop ? [{ icon: <MoveToTopIcon />, onClick: moveToTop }] : []),
            { icon: <DeleteIcon />, onClick: onDelete, className: styles['btn-delete'], 'data-btn-delete': true }
          ] as ActionsStripAction[]}
      />
    </li>
  )
}
